import { Autocomplete } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import PmiTextField from 'shell/components/shared/text-field';
import debounce from 'shell/utils/debounce';
import { noop } from 'shell/utils/noop';
import { Operation } from 'shell/view-models/operation';
import { Region } from 'shell/models/region';
import { Province } from 'shell/models/province';
import { EndedMunicipality } from 'shell/models/ended-municipality';
import { MunicipalityConsulate } from 'shell/models/municipality';
import { Consulate } from 'shell/models/person/shared';
import { DateTime } from 'luxon';

interface MunicipalityForeignListProps {
  disabled?: boolean;
  value?: MunicipalityForeignAnprVM | null;
  onChange: (municipality: MunicipalityForeignAnprVM | null) => void;
  error?: boolean;
  required?: boolean;
  label?: string;
  id?: string;
  foreignStateIstatCode?: string;
  fromDate?: DateTime | null;
}

interface Key {
  codice?: number;
}

interface MunicipalityForeignAnpr {
  nome?: string;
  provincia?: string;
  codIstat?: string;
  codFiscale?: string;
  cdNazione: string;
  cdTipoComune: string;
  key: Key;
  idProvincia?: number;
  statoEsteroVO: {
    nome: string;
  };
  attivo: string;
  cap: string;
  capoluogo: string;
  codConsolato: number;
  codNuovoComune: number;
  codTerritorio: number;
  codiceErrore: number;
  codiceOperatoreInserimento: string;
  codiceOperatoreUltimoAggiornamento: string;
  codiceTerritorio: number;
  competenzaConsolatoSuTerritorio: string;
  comuneCessato: [EndedMunicipality];
  comuneTerritorioConsolato?: MunicipalityConsulate;
  consolato: Consulate;
  data: string;
  dataCessazione: string;
  dataIstituz: string;
  flag: string;
  flagErrore: string;
  italiano: null;
  note: string;
  oldCode: string;
  provinciaVO: Province;
  regione: string;
  regioneVO: Region;
  timestampInserimento: string;
  timestampUltimoAggiornamento: string;
  tipoCessazione: string;
  tribunale: number;
}

export interface MunicipalityForeignAnprVM {
  id?: string;
  description: string;
  istatCode?: string;
  province: string;
  state?: string;
  provinceCode?: string;
  startDate?: string;
  endDate?: string;
  idProvince?: number;
  el?: MunicipalityForeignAnpr;
}

const MunicipalityForeignListComponent = ({
  disabled,
  onChange,
  value: selectedValue,
  error,
  required,
  label = 'Comuni esteri',
  id = 'municipality',
  foreignStateIstatCode,
  fromDate,
}: MunicipalityForeignListProps): React.ReactElement => {
  const [municipalities, getMunicipalities] = useHttpFetch<
    HttpResponseList<MunicipalityForeignAnpr>,
    Array<MunicipalityForeignAnprVM>
  >(`${__MUNICIPALITY_API_URL__}/geo/comune/stato-estero/${foreignStateIstatCode}`, {
    onBootstrap: false,
    initialData: [],
    options: {
      loading: false,
      errorCatching: true,
    },
    transformer: (response: AxiosResponse<HttpResponseList<MunicipalityForeignAnpr>>) => {
      const { data } = response;

      return (
        (data?.payload?.list?.map((item) => ({
          id: item.key?.codice?.toString() || '',
          istatCode: item.codIstat,
          description: item.nome,
          provinceCode: item.provincia || '',
          province: item.provincia,
          state: item.statoEsteroVO?.nome || '',
          el: item,
        })) as Array<MunicipalityForeignAnprVM>) || []
      );
    },
  });

  const disabledComponent = useMemo(() => {
    // without foreignStateIstatCode is disabled
    if (!foreignStateIstatCode) {
      return true;
    }

    return disabled || false;
  }, [disabled, foreignStateIstatCode]);

  const defaultProps = useMemo(
    () => ({
      options: (municipalities.data as Array<MunicipalityForeignAnprVM>) || [],
      getOptionLabel: (option: MunicipalityForeignAnprVM) => {
        const municipality = option.description;

        const state = option.state ? `(${option.state})` : '';
        return `${municipality} ${state}`;
      },
    }),
    [municipalities]
  );

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        const { abort = noop } = municipalities;

        abort();

        getMunicipalities({
          params: {},
          paramsSerializer: () =>
            `filters=nome:${value}:${Operation.LIKE}${
              fromDate && fromDate.isValid
                ? `&filters=dataDal:${fromDate.toFormat('yyyy-MM-dd')}`
                : ''
            }`,
        });
      },
      [municipalities.abort, getMunicipalities, fromDate]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      disabled={disabledComponent}
      value={selectedValue}
      onChange={(event, element) => {
        event.stopPropagation();
        onChange(element);
      }}
      onInputChange={(event, value, reason) => {
        if (value.length >= 3 && reason === 'input') {
          handleSearch(value);
        }
      }}
      clearOnBlur={false}
      loading={municipalities.isLoading}
      style={{ flex: 1 }}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

MunicipalityForeignListComponent.displayName = 'Municipality Foreign list component';

const MunicipalityForeignList = memo(MunicipalityForeignListComponent);

export default MunicipalityForeignList as typeof MunicipalityForeignListComponent;
